import type { IJuryDuties } from '../jury/types';

/** True when a contest row or route param matches a locally deleted contest id/slug. */
export function isContestDeleted(contestRef: string | undefined, deletedContestIds: string[] | undefined): boolean {
    if (!contestRef || !deletedContestIds?.length) return false;
    return deletedContestIds.includes(contestRef);
}

export function contestRowIsDeleted(
    row: { _id?: string; slug?: string },
    deletedContestIds: string[] | undefined,
): boolean {
    if (!deletedContestIds?.length) return false;
    return isContestDeleted(row._id, deletedContestIds) || isContestDeleted(row.slug, deletedContestIds);
}

export function juryDutyContestIsDeleted(duty: IJuryDuties, deletedContestIds: string[] | undefined): boolean {
    const contest = duty.contest;
    if (!contest) return false;
    return contestRowIsDeleted({ _id: contest._id, slug: contest.slug || contest.contest_id }, deletedContestIds);
}

/** Immediate filter when DELETE_CONTEST_SUCCESS fires (before tombstone list is read elsewhere). */
export function juryDutyMatchesDeletedContest(duty: IJuryDuties, documentId: string, slug?: string): boolean {
    const contest = duty.contest;
    if (!contest) return false;
    const trimmedSlug = slug?.trim();
    return (
        contest._id === documentId ||
        (!!trimmedSlug && (contest.slug === trimmedSlug || contest.contest_id === trimmedSlug))
    );
}

export function registerDeletedContestIds(existing: string[] | undefined, documentId: string, slug?: string): string[] {
    const next = new Set(existing ?? []);
    next.add(documentId);
    if (slug?.trim()) next.add(slug.trim());
    return Array.from(next);
}

/** Drop tombstones for contests the dashboard API still returns (stale persisted deletes). */
export function pruneDeletedContestIdsForCampaignRows(
    deletedContestIds: string[] | undefined,
    rows: Array<{ _id?: string; slug?: string }> | undefined,
): string[] {
    if (!deletedContestIds?.length) return [];
    if (!rows?.length) return deletedContestIds;

    const liveRefs = new Set<string>();
    for (const row of rows) {
        if (row._id) liveRefs.add(row._id);
        if (row.slug?.trim()) liveRefs.add(row.slug.trim());
    }

    return deletedContestIds.filter(id => !liveRefs.has(id));
}
